import axios from 'axios'

export const baseURL = process.env.REACT_APP_BASE_URL

const jwtInterceptor = axios.create({
  baseURL,
  withCredentials: true,
})

let isRefreshing = false
let failedQueue: any[] = []

const processQueue = (error: any, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

const removeS3PresignedParams = (url: string) => {
  const urlObj = new URL(url)
  const queryParams = urlObj.searchParams
  queryParams.delete('Key-Pair-Id')
  queryParams.delete('Expires')
  queryParams.delete('Signature')
  const cleanedUrl = `${urlObj.origin}${urlObj.pathname}${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`
  return cleanedUrl
}

const cleanBodyUrl = (body: any) => {
  if (typeof body === 'object') {
    const awsPresignedRegex = /(\?|&)Expires=\d+/
    for (const key in body) {
      if (typeof body[key] === 'string' && awsPresignedRegex.test(body[key])) {
        body[key] = removeS3PresignedParams(body[key])
      }
    }
  }
  return body
}

const getCSRFToken = (): string | null => {
  const match = document.cookie.match(/csrftoken=([^;]+)/)
  return match ? match[1] : null
}

const isPublicAuthEndpoint = (url?: string): boolean => {
  if (!url) return false
  return url.includes('/auth/login') || url.includes('/auth/token/refresh')
}

jwtInterceptor.interceptors.request.use(
  config => {
    config.headers!['X-Requested-With'] = 'XMLHttpRequest'

    const csrfToken = getCSRFToken()
    if (csrfToken) {
      config.headers!['X-CSRFToken'] = csrfToken
    }

    const token = localStorage.getItem('key')
    if (token && !isPublicAuthEndpoint(config.url)) {
      config.headers!.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)
// NUEVO
jwtInterceptor.interceptors.request.use(function (request) {
  request.data = cleanBodyUrl(request.data)
  return request
})
// NUEVO

const isRefreshTokenUrl = (url: string) => {
  return url.includes('/api/auth/token/refresh')
}

jwtInterceptor.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    const originalRequest = error.config
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshing
    ) {
      if (isRefreshTokenUrl(originalRequest.url)) {
        handleLogout()
        return Promise.reject(error)
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const csrfToken = getCSRFToken()
        const refreshResponse = await axios
          .post(
            `${baseURL}/api/auth/token/refresh/`,
            {},
            {
              withCredentials: true,
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...(csrfToken && { 'X-CSRFToken': csrfToken }),
              },
            }
          )
          .then(response => {
            if (response.status !== 200) {
              handleLogout()
              return Promise.reject(response)
            }
            return response
          })
          .catch(err => {
            handleLogout()
            return Promise.reject(err)
          })

        const newToken = refreshResponse.data.access
        localStorage.setItem('key', newToken)

        jwtInterceptor.defaults.headers.common.Authorization = `Bearer ${newToken}`
        originalRequest.headers.Authorization = `Bearer ${newToken}`

        processQueue(null, newToken)
        return jwtInterceptor({
          ...originalRequest,
          headers: {
            ...originalRequest.headers,
            Authorization: `Bearer ${newToken}`,
          },
          withCredentials: true,
          method: originalRequest.method,
          url: originalRequest.url,
          data: originalRequest.data,
        })
      } catch (refreshError) {
        processQueue(refreshError, null)
        handleLogout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return jwtInterceptor({
            ...originalRequest,
            headers: {
              ...originalRequest.headers,
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
            method: originalRequest.method,
            url: originalRequest.url,
            data: originalRequest.data,
          })
        })
        .catch(err => {
          return Promise.reject(err)
        })
    }

    return Promise.reject(error)
  }
)

function handleLogout() {
  // Limpiar completamente todo el almacenamiento local
  localStorage.clear()
  sessionStorage.clear()
  window.location.href = '/'
}

export default jwtInterceptor
